import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleWhereLocalsRun() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "where-vancouver-locals-love-to-run";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park Seawall",
    "section-2": "False Creek Seawall",
    "section-3": "Pacific Spirit Regional Park",
    "section-4": "Kitsilano to Spanish Banks",
    "section-5": "Arbutus Greenway",
    "section-6": "Queen Elizabeth Park",
    "section-7": "Grouse Grind",
    "section-8": "Trout Lake & Central Valley Greenway",
    "section-9": "Burnaby Lake Regional Park",
    "section-10": "West Vancouver Seawall",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "Stanley Park – City of Vancouver",
    },
    {
      url: "https://metrovancouver.org/services/regional-parks/park/pacific-spirit-regional-park",
      label: "Pacific Spirit Regional Park (Metro Vancouver)",
    },
    {
      url: "https://runvan.org/crews/",
      label: "Local Run Crews in Vancouver (RUNVAN)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park Seawall",
    },
    {
      id: "section-2",
      lat: 49.27311,
      lng: -123.10035,
      popupText: "False Creek Seawall",
    },
    {
      id: "section-3",
      lat: 49.25000,
      lng: -123.21000,
      popupText: "Pacific Spirit Regional Park",
    },
    {
      id: "section-4",
      lat: 49.27200,
      lng: -123.19200,
      popupText: "Kitsilano to Spanish Banks",
    },
    {
      id: "section-5",
      lat: 49.26678,
      lng: -123.14100,
      popupText: "Arbutus Greenway",
    },
    {
      id: "section-6",
      lat: 49.24222,
      lng: -123.11500,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "section-7",
      lat: 49.37120,
      lng: -123.09840,
      popupText: "Grouse Grind (Grouse Mountain)",
    },
    {
      id: "section-8",
      lat: 49.25444,
      lng: -123.06111,
      popupText: "Trout Lake & Central Valley Greenway",
    },
    {
      id: "section-9",
      lat: 49.24243,
      lng: -122.94607,
      popupText: "Burnaby Lake Regional Park",
    },
    {
      id: "section-10",
      lat: 49.32477,
      lng: -123.15074,
      popupText: "West Vancouver Seawall",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>10 Best Running Spots in Vancouver – #7 Will Take Your Breath Away!</title>
        <meta
          name="description"
          content="Discover where Vancouver locals love to run with top routes like Stanley Park Seawall, Grouse Grind, and more. Practical details and insider tips included."
        />
        <meta
          name="keywords"
          content="vancouver running, stanley park seawall, grouse grind, running tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/where-vancouver-locals-love-to-run.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="10 Best Running Spots in Vancouver – #7 Will Take Your Breath Away!"
        />
        <meta
          property="og:description"
          content="Discover where Vancouver locals love to run with top routes like Stanley Park Seawall, Grouse Grind, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/where-vancouver-locals-love-to-run.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/where-vancouver-locals-love-to-run.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="10 Best Running Spots in Vancouver – #7 Will Take Your Breath Away!"
        />
        <meta
          name="twitter:description"
          content="Discover where Vancouver locals love to run, with practical tips and scenic routes."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/where-vancouver-locals-love-to-run.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Sunset silhouette of runners on the Vancouver Seawall"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is widely recognized as a runner’s paradise: from
              seawalls hugging the Pacific coast to forested trails in old-growth
              parks, there’s a route to satisfy any pace or preference. Below are
              ten of the top local-favorite running spots, each offering a unique
              Vancouver experience. Whether you prefer a breezy waterfront jog or
              a challenging mountain climb, this guide covers all the essentials
              to help you lace up and head out.
            </p>

            {/* SECTION: 1 - Stanley Park Seawall */}
            <h2 id="section-1">Stanley Park Seawall</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Stanley Park Seawall{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This is the crown jewel of Vancouver’s running routes, featuring
              a scenic 9-kilometer loop along the park perimeter. Enjoy sweeping
              ocean views, iconic landmarks like the Brockton Point Lighthouse,
              and the city skyline. The path is paved, relatively flat, and
              includes a designated pedestrian lane, making it perfect for all
              levels.
            </p>
            <p>
              <strong>Practical Info:</strong> Park hours usually 6am-10pm; paid
              parking is available, and there are plenty of water fountains and
              restrooms along the route. It can get busy on sunny weekends, so
              go early to avoid the crowds.
            </p>

            {/* SECTION: 2 - False Creek Seawall */}
            <h2 id="section-2">False Creek Seawall</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> False Creek{" "}
              <Marker
                coordY={49.27311}
                coordX={-123.10035}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Another popular city route that wraps around the bustling inlet
              of False Creek. You’ll pass by Science World, Olympic Village, and
              Granville Island with vibrant scenery and plenty of amenities
              nearby. The ~9km loop is flat, paved, and well-lit, making it a
              convenient choice for an evening run.
            </p>
            <p>
              <strong>Highlights:</strong> Urban vibes, waterfront views, and
              lots of cafes and breweries to reward yourself post-run. Expect
              mild congestion during peak hours.
            </p>

            {/* SECTION: 3 - Pacific Spirit Regional Park */}
            <h2 id="section-3">Pacific Spirit Regional Park</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Pacific Spirit Park near UBC{" "}
              <Marker
                coordY={49.25000}
                coordX={-123.21000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – For a dose of nature, venture into this lush network of over 55km
              of trails. You’ll run under towering cedars and firs, enjoying soft
              dirt paths that are gentle on your joints. Keep a map or GPS handy,
              as trails intersect frequently. 
            </p>
            <p>
              <strong>Key Tips:</strong> No facilities inside the park, so carry
              water; free parking at various trailheads. Be mindful of wildlife
              and trail etiquette.
            </p>

            {/* SECTION: 4 - Kitsilano to Spanish Banks */}
            <h2 id="section-4">Kitsilano to Spanish Banks</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Kits Beach to Spanish Banks{" "}
              <Marker
                coordY={49.27200}
                coordX={-123.19200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This seaside route is about 7-8km one-way, combining paved and
              gravel paths along some of Vancouver’s most beloved beaches. Enjoy
              panoramic ocean views with the North Shore mountains as the
              backdrop.
            </p>
            <p>
              <strong>Why Locals Love It:</strong> It’s perfect for sunset
              runs, offers beach amenities (restrooms, water fountains), and the
              soft ocean breeze. 
            </p>

            {/* SECTION: 5 - Arbutus Greenway */}
            <h2 id="section-5">Arbutus Greenway</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Former railway corridor from False
              Creek to the Fraser River{" "}
              <Marker
                coordY={49.26678}
                coordX={-123.14100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This 8.5km paved path is a car-free haven popular for longer
              training runs. It’s mostly flat with gentle grades. You’ll pass
              through various residential neighborhoods and community gardens.
            </p>
            <p>
              <strong>Tip:</strong> Not many restrooms on the path itself, but
              you’re never far from shops and cafes if you take a short detour.
            </p>

            {/* SECTION: 6 - Queen Elizabeth Park */}
            <h2 id="section-6">Queen Elizabeth Park</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Queen Elizabeth Park{" "}
              <Marker
                coordY={49.24222}
                coordX={-123.11500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A beautifully landscaped park known for its hilltop viewpoints
              and flower gardens. Short paved loops for a relaxed jog or
              challenging hill repeats if you tackle the summit.
            </p>
            <p>
              <strong>Must-See:</strong> The stunning skyline view from the top.
              Park hours are generally 6am-10pm, with pay parking lots inside.
            </p>

            {/* SECTION: 7 - Grouse Grind */}
            <h2 id="section-7">Grouse Grind</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Grouse Mountain in North Vancouver{" "}
              <Marker
                coordY={49.37120}
                coordX={-123.09840}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Nicknamed “Mother Nature’s StairMaster,” this 2.9km trail gains
              about 853m in elevation. It’s one-way up only. Highly challenging
              but rewarded with panoramic city and mountain views at the top.
            </p>
            <p>
              <strong>Important:</strong> Open seasonally (spring to fall). 
              Descend via gondola (fee applies) or the alternate BCMC Trail if
              you prefer a full mountain workout.
            </p>

            {/* SECTION: 8 - Trout Lake & Central Valley Greenway */}
            <h2 id="section-8">Trout Lake & Central Valley Greenway</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> John Hendry Park (Trout Lake) in East
              Vancouver{" "}
              <Marker
                coordY={49.25444}
                coordX={-123.06111}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A scenic 1km gravel loop around the lake with mountain views.
              Connect easily to the paved Central Valley Greenway, which extends
              ~20km into Burnaby and beyond.
            </p>
            <p>
              <strong>Amenities:</strong> Free parking, restrooms, and a
              community center on-site. Great for relaxed laps or building a
              longer run by hopping onto the Greenway.
            </p>

            {/* SECTION: 9 - Burnaby Lake Regional Park */}
            <h2 id="section-9">Burnaby Lake Regional Park</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> Burnaby Lake{" "}
              <Marker
                coordY={49.24243}
                coordX={-122.94607}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – About 10km loop trail circles this tranquil lake, abundant with
              birdlife. Primarily soft gravel/dirt, it’s easy on the knees with
              minimal elevation gain.
            </p>
            <p>
              <strong>Highlight:</strong> Ideal for distance training with no
              car traffic or road crossings.
            </p>

            {/* SECTION: 10 - West Vancouver Seawall */}
            <h2 id="section-10">West Vancouver Seawall (Ambleside to Dundarave)</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> West Vancouver Seawall{" "}
              <Marker
                coordY={49.32477}
                coordX={-123.15074}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A 5km paved waterfront path from Ambleside Park to Dundarave
              Pier, offering panoramic views of Burrard Inlet, the Lions Gate
              Bridge, and the city skyline. 
            </p>
            <p>
              <strong>Why Go:</strong> Less crowded than downtown routes, serene
              ocean vistas, and charming neighborhood vibes.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Most Vancouver parks are open from around 6am to 10pm. Always check
              official resources for seasonal or weather-related closures
              (especially Grouse Grind). Run on the right side of multi-use paths
              and yield to pedestrians when overtaking. In forested areas, watch
              for wildlife and follow posted signs regarding coyotes or bears.
              Use caution on unlit routes at night, and consider reflective gear
              or running in groups.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Can I run year-round in Vancouver?</h3>
              <p>
                Yes! Vancouver’s mild climate allows outdoor running even in
                winter. It can be rainy, so invest in waterproof gear and trail
                shoes for muddy paths.
              </p>

              <h3>2. What are the most popular races in Vancouver?</h3>
              <p>
                The Vancouver Sun Run 10K, BMO Vancouver Marathon, and the
                lululemon SeaWheeze Half Marathon are a few highlights. They
                often sell out quickly, so register early.
              </p>

              <h3>3. Are there running clubs I can join?</h3>
              <p>
                Yes, many free clubs like Running Room group runs, Vancouver
                Frontrunners, and East Van Run Crew welcome runners of all
                levels. It’s a great way to explore new routes and meet people.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver boasts a remarkable variety of running routes that blend
              urban convenience with stunning natural settings. Whether you’re a
              seasoned marathoner or a casual jogger, these top ten locales offer
              something for everyone. Grab your running shoes, pick a path, and
              experience why Vancouver is often hailed as one of the world’s best
              running cities.
            </p>
            <p>
              <em>
                Always check official park websites for up-to-date trail and
                safety information. Happy running!
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}